import React, { useState, useRef } from "react";
import {
  Autocomplete,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
} from "@mui/material";
import MultiSelectDropdown from "./common/MultiSelectDropdown";

const AUDIENCE_OPTIONS = [
  { value: "venues", label: "Upcoming events (by venue)" },
  { value: "all_users", label: "All users" },
  { value: "all_djs", label: "All DJs" },
];

export default function NotificationModal(props) {
  const titleInputRef = useRef();
  const bodyInputRef = useRef();
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [audience, setAudience] = useState("venues");
  const shouldShowVenuesField = !!props.showVenuesField;
  const [step, setStep] = useState("form");
  const [preview, setPreview] = useState(null);
  const [state, setState] = useState({});

  const handlePreview = () => {
    const validationErrors = {};

    if (!titleInputRef.current.value) {
      validationErrors.title = "Title is required.";
    }

    if (!bodyInputRef.current.value) {
      validationErrors.body = "Body is required.";
    }

    if (shouldShowVenuesField && audience === "venues" && !selectedVenues.length) {
      validationErrors.venues = "Please select at least one venue.";
    }

    if (Object.keys(validationErrors).length) {
      setState((s) => ({ ...s, errors: validationErrors }));
      return;
    }

    setState((s) => ({ ...s, errors: undefined }));
    setPreview({
      title: titleInputRef.current.value,
      body: bodyInputRef.current.value,
      selectedVenues,
      audience,
    });
    setStep("preview");
  };

  const handleSend = async () => {
    setState((s) => ({ ...s, isLoading: true, errors: undefined }));
    const body = {
      userId: props.user.id,
      title: preview.title,
      body: preview.body,
      ...(shouldShowVenuesField ? { audience: preview.audience } : {}),
      ...(shouldShowVenuesField && preview.audience === "venues"
        ? { venueIds: preview.selectedVenues.map((venue) => venue.id) }
        : {}),
    };
    try {
      await props.handleConfirm(body);
      props.handleClose();
    } catch (error) {
      setState((s) => ({
        ...s,
        isLoading: false,
        errors: {
          submit: error?.message || error || "Failed to send notification.",
        },
      }));
    }
  };

  return (
    <Dialog
      open={true}
      onClose={props.handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: { backgroundColor: "#d8c8f4", color: "#4b445b" } }}
    >
      {step === "form" ? (
        <>
          <DialogTitle>Send Custom Notification</DialogTitle>
          <DialogContent>
            <TextField
              autoComplete="off"
              margin="dense"
              id="title"
              label="Title"
              type="text"
              fullWidth
              variant="outlined"
              inputRef={titleInputRef}
              defaultValue={preview?.title}
              required
              error={!!state.errors?.title}
              helperText={state.errors?.title}
            />
            <TextField
              autoComplete="off"
              margin="dense"
              id="body"
              label="Body"
              type="text"
              fullWidth
              variant="outlined"
              inputRef={bodyInputRef}
              defaultValue={preview?.body}
              multiline
              rows={6}
              required
              error={!!state.errors?.body}
              helperText={state.errors?.body}
            />
            {shouldShowVenuesField && (
              <Autocomplete
                disablePortal
                id="notification-audience"
                options={AUDIENCE_OPTIONS}
                value={AUDIENCE_OPTIONS.find((option) => option.value === audience)}
                disableClearable
                onChange={(event, option) => setAudience(option.value)}
                renderInput={(params) => (
                  <TextField
                    margin="dense"
                    fullWidth
                    variant="outlined"
                    label="Audience"
                    id="audience"
                    type="text"
                    {...params}
                  />
                )}
              />
            )}
            {shouldShowVenuesField && audience === "venues" && (
              <>
                <MultiSelectDropdown
                  id="notification-venues"
                  label="Venues"
                  options={props.venueOptions || []}
                  value={selectedVenues}
                  onChange={setSelectedVenues}
                  placeholder="Select one or more venues"
                  closeOnSelect
                />
                {!!state.errors?.venues && (
                  <Typography variant="caption" color="error" sx={{ display: "block", marginTop: 1 }}>
                    {state.errors.venues}
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={props.handleClose} variant="contained">
              Cancel
            </Button>
            <Button onClick={handlePreview} variant="contained">
              Preview
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Notification Preview</DialogTitle>
          <DialogContent>
            <Typography variant="caption" sx={{ color: "#6b6478" }}>
              This is how the notification will appear
            </Typography>

            <Divider sx={{ borderColor: "rgba(75,68,91,0.2)", my: 1.5 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              {preview.title}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
              {preview.body}
            </Typography>

            {shouldShowVenuesField && preview.audience === "all_users" && (
              <Typography variant="caption" sx={{ color: "#6b6478", display: "block" }}>
                Targeting: All users
              </Typography>
            )}
            {shouldShowVenuesField && preview.audience === "all_djs" && (
              <Typography variant="caption" sx={{ color: "#6b6478", display: "block" }}>
                Targeting: All DJs
              </Typography>
            )}
            {shouldShowVenuesField &&
              preview.audience === "venues" &&
              preview.selectedVenues.length > 0 && (
                <Typography variant="caption" sx={{ color: "#6b6478", display: "block" }}>
                  Targeting attendees of: {preview.selectedVenues.map((v) => v.label).join(", ")}
                </Typography>
              )}

            {!!state.errors?.submit && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {state.errors.submit}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep("form")} variant="contained" disabled={state.isLoading}>
              Back
            </Button>
            <Button onClick={handleSend} variant="contained" disabled={state.isLoading}>
              Confirm & Send
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
